<template>
    <div
        v-if="isVisible"
        class="fulltext-search-overlay top-[52px] left-0 right-0 bg-white pt-4 pb-2 z-40"
    >
        <FulltextSearchContainer
            ref="fulltextSearchContainerRef"
            v-model="modelValueLocal"
            :placeholder="placeholder"
            :accessibility-label="accessibilityLabel"
            :view-all-url="viewAllUrl"
            :view-all-text="viewAllText"
            :show-search-results="showSearchResults"
            :search-results="searchResults"
            :auto-focus="autoFocus"
            results-container-class="mt-1"
            class="mx-auto px-4 max-[727px]:max-w-[600px] min-[728px]:max-w-unset"
            @search-performed="emit('search-performed')"
            @result-selected="emit('result-selected', $event)"
            @close-results="emit('close-results')"
        />
    </div>
</template>

<script setup lang="ts">
    import { ref, computed } from 'vue';
    import { useFocus, useVModel, whenever } from '@vueuse/core';
    import FulltextSearchContainer from './FulltextSearchContainer.vue';
    import type { SearchResult } from '~/types/fulltextSearch';

    type FulltextSearchOverlayProps = {
        modelValue: string;
        isVisible: boolean;
        placeholder: string;
        accessibilityLabel: string;
        viewAllUrl: string;
        viewAllText: string;
        showSearchResults: boolean;
        searchResults: SearchResult[];
        autoFocus?: boolean;
    }

    const props = withDefaults(defineProps<FulltextSearchOverlayProps>(), {
        autoFocus: true,
    });

    type FulltextSearchOverlayEmits = {
        'update:modelValue': [value: string];
        'result-selected': [result: SearchResult];
        'search-performed': [];
        'close-results': [];
    }

    const emit = defineEmits<FulltextSearchOverlayEmits>();

    const modelValueLocal = useVModel(props, 'modelValue', emit);

    const fulltextSearchContainerRef = ref<InstanceType<typeof FulltextSearchContainer> | null>(null);

    const searchInputElement = computed(() =>
        fulltextSearchContainerRef.value?.$el?.querySelector?.('input') as HTMLInputElement
    );
    const { focused } = useFocus(searchInputElement, { initialValue: false });

    whenever(
        () => props.isVisible && props.autoFocus,
        () => focused.value = true,
        { flush: 'post' }
    );
</script>
